import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Search, Loader2, MapPin, Check } from 'lucide-react';
import { superadminApi } from '../lib/api';

const AddressLookup = ({ 
  onAddressSelect,
  initialPostcode = '',
  disabled = false,
  onPostcodeChange,
  label = 'Postcode'
}) => {
  const [apiKey, setApiKey] = useState(null);
  const [postcode, setPostcode] = useState(initialPostcode);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  // Fetch API key on mount
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await superadminApi.getAddressApiKey();
        setApiKey(response.data.key);
      } catch (err) {
        console.error('Failed to fetch address API key:', err);
      }
    };
    fetchApiKey();
  }, []);

  // ⚠️ `useState(initialPostcode)` ONLY EVER READS THAT PROP ONCE — at mount. Every
  // caller loads its data AFTER the first render, so without this the postcode box is
  // permanently blank no matter what the record holds.
  //
  // Rob found it on the signup wizard, 13/08/2026: every other field came back from the
  // saved company and the postcode did not. It was the only one living inside a child
  // component that had taken a snapshot. **A prop called `initial*` is a promise the
  // component keeps too literally.**
  //
  // ⚠️ IT BITES THE EMPLOYEE DIALOG HARDER. `AddEditEmployeeDialog` is one component
  // reused for every employee — open Alice, close, open Bob, and Bob would be shown
  // Alice's postcode until something forced a remount.
  //
  // ⚠️ ONLY WHEN IT ACTUALLY DIFFERS. Typing pushes each keystroke up via
  // `onPostcodeChange`, and the parent hands the same value back down; syncing
  // unconditionally would fight the person typing.
  useEffect(() => {
    const incoming = initialPostcode || '';
    setPostcode((current) => (incoming === (current || '') ? current : incoming));
  }, [initialPostcode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddresses = useCallback(async () => {
    if (!postcode || postcode.length < 5 || !apiKey) return;
    
    setSearching(true);
    setError(null);
    setAddresses([]);

    try {
      // Royal Mail AddressNow API - Find endpoint
      const response = await fetch(
        `https://api.addressnow.co.uk/Capture/Interactive/Find/v1.00/json3.ws?` +
        `Key=${apiKey}&Text=${encodeURIComponent(postcode)}&IsMiddleware=True&` +
        `Countries=GB&Limit=50&Language=en`
      );
      
      const data = await response.json();
      
      if (data.Items && data.Items.length > 0) {
        // Filter to only show retrievable addresses (not container items)
        const retrievableAddresses = data.Items.filter(item => item.Type === 'Address');
        
        if (retrievableAddresses.length > 0) {
          setAddresses(retrievableAddresses);
          setShowResults(true);
        } else {
          // If no direct addresses, we need to drill down
          // Get the first container and retrieve its contents
          const containers = data.Items.filter(item => item.Type !== 'Address');
          if (containers.length > 0) {
            await drillDown(containers[0].Id);
          }
        }
      } else {
        setError('No addresses found for this postcode');
      }
    } catch (err) {
      console.error('Address search error:', err);
      setError('Failed to search addresses');
    } finally {
      setSearching(false);
    }
  // `drillDown` is recreated each render; including it would rebuild this on every pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode, apiKey]);

  const drillDown = async (containerId) => {
    try {
      const response = await fetch(
        `https://api.addressnow.co.uk/Capture/Interactive/Find/v1.00/json3.ws?` +
        `Key=${apiKey}&Container=${containerId}&IsMiddleware=True&` +
        `Countries=GB&Limit=50&Language=en`
      );
      
      const data = await response.json();
      
      if (data.Items && data.Items.length > 0) {
        const retrievableAddresses = data.Items.filter(item => item.Type === 'Address');
        if (retrievableAddresses.length > 0) {
          setAddresses(retrievableAddresses);
          setShowResults(true);
        } else {
          // Keep drilling down
          const containers = data.Items.filter(item => item.Type !== 'Address');
          if (containers.length > 0) {
            await drillDown(containers[0].Id);
          }
        }
      }
    } catch (err) {
      console.error('Drill down error:', err);
    }
  };

  const selectAddress = async (address) => {
    setLoading(true);
    try {
      // Retrieve full address details
      const response = await fetch(
        `https://api.addressnow.co.uk/Capture/Interactive/Retrieve/v1.00/json3.ws?` +
        `Key=${apiKey}&Id=${address.Id}`
      );
      
      const data = await response.json();
      
      if (data.Items && data.Items.length > 0) {
        const fullAddress = data.Items[0];
        onAddressSelect({
          address_line_1: fullAddress.Line1 || '',
          address_line_2: fullAddress.Line2 || '',
          city: fullAddress.City || fullAddress.PostTown || '',
          county: fullAddress.Province || fullAddress.AdminAreaName || '',
          postcode: fullAddress.PostalCode || '',
          country: fullAddress.CountryName || 'United Kingdom'
        });
        setShowResults(false);
        setPostcode(fullAddress.PostalCode || postcode);
      }
    } catch (err) {
      console.error('Failed to retrieve address:', err);
      setError('Failed to retrieve address details');
    } finally {
      setLoading(false);
    }
  };

  if (!apiKey) {
    return null; // Don't show if API key not configured
  }

  return (
    <div ref={containerRef} className="relative">
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input
          type="text"
          value={postcode}
          onChange={(e) => { const v = e.target.value.toUpperCase(); setPostcode(v); if (onPostcodeChange) onPostcodeChange(v); }}
          placeholder="Enter postcode..."
          className="input-hive"
          disabled={disabled || loading}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddresses())}
        />
        <Button
          type="button"
          onClick={searchAddresses}
          disabled={disabled || loading || searching || postcode.length < 5}
          className="btn-secondary"
        >
          {searching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </Button>
      </div>
      
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
      
      {/* Address Results Dropdown */}
      {showResults && addresses.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {addresses.map((addr, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => selectAddress(addr)}
              disabled={loading}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary/50 flex items-center gap-2 border-b border-border last:border-b-0"
            >
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{addr.Text || addr.Description}</span>
              {loading && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressLookup;
